import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import { customerService } from '../../services/api';
import { getCountryOptions, LOCATION_LIST_COUNTRIES } from '../../utils/countries';
import { matchKey } from '../../utils/formatters';

// Address form selects (country / state / city), shared by the customer and
// business forms. Codes are stored, names are displayed: country names come
// from Intl.DisplayNames in the UI language; states and cities are proper
// nouns (not translated) and come from the backend per country (BR: IBGE).
// Countries without option lists keep free-text state/city.

const optionFilter = createFilterOptions({ stringify: (option) => `${option.name} ${option.code}` });

// TextField props coming from the forms' fieldProps() that the Autocomplete
// owns instead (value/onChange/input slots).
const inputFieldProps = ({ value, onChange, slotProps, inputProps, ...rest }) => rest;

const useLocationOptions = (kind, country, state) => {
  const listed = LOCATION_LIST_COUNTRIES.includes(country);
  const enabled = listed && (kind === 'states' || Boolean(state));
  const query = useQuery({
    queryKey: ['locations', kind, country, kind === 'cities' ? state : null],
    queryFn: () =>
      (kind === 'states'
        ? customerService.getLocationStates(country)
        : customerService.getLocationCities(country, state)
      ).then((res) => res.data),
    enabled,
    // These lists barely change (the backend caches them for 30 days).
    staleTime: Infinity,
    retry: (count, error) => (error?.response?.status ?? 0) >= 500 && count < 1,
  });
  const notListed = !listed || query.error?.response?.status === 404;
  return {
    options: query.data ?? [],
    // Free text when the country has no lists, or (temporarily, with a hint)
    // when the provider is down.
    supported: !notListed && !query.isError,
    unavailable: listed && query.isError && !notListed,
    loading: enabled && query.isLoading,
    ready: enabled && query.isSuccess,
  };
};

/**
 * State/city options of an address plus list validation. Saved (legacy) or
 * autofilled values are matched accent/case-insensitively against the list
 * and normalized (e.g. "sao paulo" -> "SP" / "São Paulo"); values that don't
 * match get `issue(field)` = { key: 'selectFromList' }.
 */
export function useAddressLocations({
  values,
  setValues,
  countryField = 'address_country',
  stateField = 'address_state',
  cityField = 'address_city',
}) {
  const country = values[countryField];
  const stateValue = values[stateField];
  const cityValue = values[cityField];
  const states = useLocationOptions('states', country);
  const cities = useLocationOptions('cities', country, stateValue);

  useEffect(() => {
    if (!states.ready || !stateValue || states.options.some((option) => option.code === stateValue)) return;
    const key = matchKey(stateValue);
    const match = states.options.find((option) => matchKey(option.code) === key || matchKey(option.name) === key);
    if (match) {
      setValues((prev) => (prev[stateField] === stateValue ? { ...prev, [stateField]: match.code } : prev));
    }
  }, [states.ready, states.options, stateValue, stateField, setValues]);

  useEffect(() => {
    if (!cities.ready || !cityValue || cities.options.some((option) => option.name === cityValue)) return;
    const key = matchKey(cityValue);
    const match = cities.options.find((option) => matchKey(option.name) === key);
    if (match) {
      setValues((prev) => (prev[cityField] === cityValue ? { ...prev, [cityField]: match.name } : prev));
    }
  }, [cities.ready, cities.options, cityValue, cityField, setValues]);

  const issue = (field) => {
    if (field === stateField && states.ready && stateValue) {
      return states.options.some((option) => option.code === stateValue) ? null : { key: 'selectFromList' };
    }
    if (field === cityField && cities.ready && cityValue) {
      return cities.options.some((option) => option.name === cityValue) ? null : { key: 'selectFromList' };
    }
    return null;
  };

  return { states, cities, issue };
}

export function CountryAutocomplete({ value, onChange, textFieldProps }) {
  const { i18n } = useTranslation();
  const options = useMemo(() => getCountryOptions(i18n.language), [i18n.language]);
  const selected = options.find((option) => option.code === value) ?? null;
  return (
    <Autocomplete
      options={options}
      value={selected}
      onChange={(event, option) => onChange(option?.code ?? '')}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, current) => option.code === current.code}
      filterOptions={optionFilter}
      autoHighlight
      disabled={textFieldProps.disabled}
      // Name only: flag emojis render as the bare country code on Windows.
      renderOption={({ key, ...props }, option) => (
        <li key={key} {...props}>
          {option.name}
        </li>
      )}
      renderInput={(params) => <TextField {...params} {...inputFieldProps(textFieldProps)} />}
    />
  );
}

function LocationAutocomplete({ locations, value, valueKey, onChange, textFieldProps, disabled, helperText }) {
  const { t } = useTranslation();
  if (!locations.supported) {
    return (
      <TextField
        {...textFieldProps}
        helperText={locations.unavailable ? t('address.locations.unavailable') : textFieldProps.helperText}
      />
    );
  }
  const selected =
    locations.options.find((option) => option[valueKey] === value) ??
    // A saved value not in the list stays visible (with the list error).
    (value ? { code: value, name: value } : null);
  return (
    <Autocomplete
      options={locations.options}
      value={selected}
      onChange={(event, option) => onChange(option?.[valueKey] ?? '')}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, current) => option[valueKey] === current[valueKey]}
      filterOptions={optionFilter}
      autoHighlight
      loading={locations.loading}
      loadingText={t('address.locations.loading')}
      noOptionsText={t('address.locations.noOptions')}
      disabled={textFieldProps.disabled || disabled}
      renderOption={({ key, ...props }, option) => (
        <li key={`${key}-${option.code}`} {...props}>
          {option.name}
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} {...inputFieldProps(textFieldProps)} helperText={helperText ?? textFieldProps.helperText} />
      )}
    />
  );
}

// Stores the state code (BR: UF "SP").
export function StateAutocomplete({ locations, value, onChange, textFieldProps }) {
  return (
    <LocationAutocomplete
      locations={locations}
      value={value}
      valueKey="code"
      onChange={onChange}
      textFieldProps={textFieldProps}
    />
  );
}

// Stores the city name (official IBGE spelling for BR). Needs the state.
export function CityAutocomplete({ locations, state, value, onChange, textFieldProps }) {
  const { t } = useTranslation();
  const needsState = locations.supported && !state;
  return (
    <LocationAutocomplete
      locations={locations}
      value={value}
      valueKey="name"
      onChange={onChange}
      textFieldProps={textFieldProps}
      disabled={needsState}
      helperText={needsState && !textFieldProps.error ? t('address.locations.selectStateFirst') : undefined}
    />
  );
}

const POSTAL_CODE_STATUS_TEXT = {
  loading: 'address.postalCodeLookup.loading',
  notFound: 'address.postalCodeLookup.notFound',
  unavailable: 'address.postalCodeLookup.unavailable',
};

/**
 * Postal code input with the autofill feedback (`usePostalCodeLookup`
 * status): a spinner while looking up, and a hint when the code isn't found
 * or the lookup is down — the user then fills the address in by hand.
 * Field errors (required, max length...) take precedence over the hint.
 */
export function PostalCodeField({ fieldProps, status }) {
  const { t } = useTranslation();
  const statusText = POSTAL_CODE_STATUS_TEXT[status];
  const slotProps = fieldProps.slotProps ?? {};
  return (
    <TextField
      {...fieldProps}
      helperText={!fieldProps.error && statusText ? t(statusText) : fieldProps.helperText}
      slotProps={{
        ...slotProps,
        input: {
          ...slotProps.input,
          endAdornment:
            status === 'loading' ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : undefined,
        },
      }}
    />
  );
}
